/**
 * Intentionally browser-local (not IST): the kickoff picker is a datetime-local input the scheduler
 * fills in their own wall-clock; Date parsing/formatting here must round-trip with that same zone.
 * The persisted value stays ISO-8601 UTC (datetimeLocalValueToIso); display elsewhere goes through formatDdMmYyyy*.
 *
 * datetime-local value (YYYY-MM-DDTHH:mm) <-> ISO string for persistence in scoping profile JSON.
 */
export function isoToDatetimeLocalValue(iso) {
  if (!iso || typeof iso !== "string") return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function datetimeLocalValueToIso(localValue) {
  if (!localValue || typeof localValue !== "string") return "";
  const d = new Date(localValue);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString();
}

function formatGoogleCalendarSegment(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return (
    d.getFullYear() +
    pad(d.getMonth() + 1) +
    pad(d.getDate()) +
    "T" +
    pad(d.getHours()) +
    pad(d.getMinutes()) +
    "00"
  );
}

/** Dedupe and filter guest emails for Google Calendar `add` (comma-separated). */
function normalizeGuestEmailsForCalendar(guestEmails) {
  if (!Array.isArray(guestEmails) || guestEmails.length === 0) return [];
  const seen = new Set();
  const out = [];
  for (const raw of guestEmails) {
    const e = String(raw || "")
      .trim()
      .toLowerCase();
    if (!e || !e.includes("@") || seen.has(e)) continue;
    seen.add(e);
    out.push(e);
  }
  return out;
}

/**
 * Opens Google Calendar "create event" with pre-filled fields (no OAuth required).
 * @param {{ title: string, details?: string, start: Date, end: Date, guestEmails?: string[] }} opts
 */
export function buildGoogleCalendarTemplateUrl(opts) {
  const { title, details = "", start, end, guestEmails } = opts;
  if (!start || Number.isNaN(start.getTime())) return "";
  const endDt = end && !Number.isNaN(end.getTime()) ? end : new Date(start.getTime() + 60 * 60 * 1000);
  const dates = `${formatGoogleCalendarSegment(start)}/${formatGoogleCalendarSegment(endDt)}`;
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: title || "Kick-off meeting",
    dates,
    details,
  });
  const guests = normalizeGuestEmailsForCalendar(guestEmails);
  if (guests.length > 0) {
    params.set("add", guests.join(","));
  }
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}
